import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExecutionScoreCard, GRADE_DOT, GRADE_SOFT, GRADE_TEXT } from "@/components/ExecutionScore";
import { LeadershipRankList } from "@/components/LeadershipScore";
import { StatusBadge } from "@/components/Badges";
import { cn } from "@/lib/utils";
import { Building2, LayoutGrid, TrendingDown, Trophy, AlertTriangle, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function OwnerDashboard() {
  const [m, setM] = useState(null);
  const [pareto, setPareto] = useState(null);
  const [leadership, setLeadership] = useState(null);
  const [cell, setCell] = useState(null);
  const [cellTasks, setCellTasks] = useState(null);

  useEffect(() => {
    api.get("/metrics/matrix").then((r) => setM(r.data));
    api.get("/metrics/pareto").then((r) => setPareto(r.data));
    api.get("/metrics/leadership", { params: { scope: "all" } }).then((r) => setLeadership(r.data));
  }, []);

  const openCell = async (branch, division) => {
    setCell({ branch, division }); setCellTasks(null);
    const r = await api.get("/metrics/tasks", { params: { branch_id: branch.id, division_id: division.id } });
    setCellTasks(r.data);
  };

  if (!m) return <div className="text-slate-400 py-10 text-center">Memuat dashboard owner...</div>;

  const download = async (fmt) => {
    try {
      const res = await api.get(`/export/execution.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `skor_eksekusi_zoa.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${fmt.toUpperCase()} diunduh`);
    } catch (e) { toast.error("Gagal mengekspor laporan"); }
  };

  const branchName = (id) => m.branches.find((b) => b.id === id)?.name || "—";
  const divName = (id) => m.divisions.find((d) => d.id === id)?.name || "—";
  const cellMap = {};
  m.matrix.forEach((c) => { cellMap[`${c.branch_id}|${c.division_id}`] = c; });
  const activeBranches = m.branches.filter((b) => m.matrix.some((c) => c.branch_id === b.id));
  const activeDivs = m.divisions.filter((d) => m.matrix.some((c) => c.division_id === d.id));

  const branchRank = Object.entries(m.branch_scores).filter(([k]) => k !== "none")
    .map(([k, v]) => ({ id: k, name: branchName(k), ...v })).sort((a, b) => b.score - a.score);
  const divRank = Object.entries(m.division_scores).filter(([k]) => k !== "none")
    .map(([k, v]) => ({ id: k, name: divName(k), ...v })).sort((a, b) => b.score - a.score);

  return (
    <div className="space-y-8 zoa-fade-up">
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" />Ekspor Excel</Button>
        <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1.5 text-rose-600" />Ekspor PDF</Button>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <ExecutionScoreCard data={m.company} title="Skor Eksekusi Perusahaan" />
        <RankCard title="Peringkat Cabang" icon={Building2} rows={branchRank} testid="branch-rank" />
        <RankCard title="Peringkat Divisi" icon={LayoutGrid} rows={divRank} testid="division-rank" />
      </div>

      {/* Leadership ranking */}
      {leadership?.leaders?.length > 0 && (
        <div data-testid="owner-leadership-section">
          <LeadershipRankList leaders={leadership.leaders} meId={leadership.me?.user_id} title="Peringkat Skor Kepemimpinan Atasan" />
        </div>
      )}

      {/* Heatmap matrix */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <LayoutGrid className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Matriks Cabang × Divisi</h3>
          <span className="text-xs text-slate-400">(klik sel untuk rincian)</span>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="owner-matrix">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-4 py-2.5 text-xs uppercase text-slate-500 sticky left-0 bg-slate-50">Cabang \ Divisi</th>
                {activeDivs.map((d) => <th key={d.id} className="px-2 py-2.5 text-xs font-semibold text-slate-600 whitespace-nowrap">{d.code || d.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {activeBranches.map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-700 sticky left-0 bg-white whitespace-nowrap">{b.name}</td>
                  {activeDivs.map((d) => {
                    const c = cellMap[`${b.id}|${d.id}`];
                    if (!c) return <td key={d.id} className="text-center text-slate-200">–</td>;
                    return (
                      <td key={d.id} className="p-1.5 text-center">
                        <button onClick={() => openCell(b, d)} data-testid={`matrix-cell-${b.id}-${d.id}`}
                          className={cn("w-full rounded-lg border px-2 py-1.5 transition-all hover:scale-105 hover:shadow-md", GRADE_SOFT[c.grade.color])}>
                          <span className="block font-extrabold leading-none">{c.score}</span>
                          <span className="block text-[10px] opacity-70">{c.task_count} tugas{c.overdue > 0 ? ` · ${c.overdue}⚠` : ""}</span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
          {[["emerald", "A ≥85"], ["sky", "B 70–84"], ["amber", "C 55–69"], ["rose", "D <55"]].map(([col, lbl]) => (
            <span key={col} className="inline-flex items-center gap-1.5"><span className={cn("h-3 w-3 rounded", GRADE_DOT[col])} />{lbl}</span>
          ))}
        </div>
      </div>

      {/* Pareto */}
      {pareto && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="h-4 w-4 text-slate-400" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Pareto Penyebab Keterlambatan</h3>
            <span className="text-xs text-slate-400">({pareto.total_reports} laporan)</span>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="pareto-chart">
            {pareto.items.length === 0 ? <p className="text-sm text-slate-400 italic">Belum ada laporan keterlambatan.</p> :
              pareto.items.map((it) => (
                <div key={it.category} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-600 font-medium">{it.category}</span>
                    <span className="text-slate-400">{it.count}× · kumulatif {it.cumulative_pct}%</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-orange-400" style={{ width: `${it.pct}%` }} />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Drill-down dialog */}
      <Dialog open={!!cell} onOpenChange={(o) => !o && setCell(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto zoa-scroll" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{cell?.branch.name} · {cell?.division.name}</DialogTitle></DialogHeader>
          {!cellTasks ? <p className="text-slate-400 py-6 text-center">Memuat...</p> : (
            <div className="space-y-2">
              {cellTasks.length === 0 && <p className="text-slate-400 text-sm">Tidak ada tugas.</p>}
              {cellTasks.map((t) => (
                <div key={t.id} className="rounded-xl border border-slate-200 p-3" data-testid={`drill-task-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-700 truncate">{t.title}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{t.assignee?.name || "—"} · {t.progress}%{t.is_overdue ? " · terlambat" : ""}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RankCard({ title, icon: Icon, rows, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-slate-400" />
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
      </div>
      <div className="space-y-2.5">
        {rows.map((r, i) => (
          <div key={r.id} className="flex items-center gap-2.5">
            <span className="text-xs font-bold text-slate-400 w-4">{i === 0 ? "🥇" : i + 1}</span>
            <span className="text-sm text-slate-700 flex-1 truncate">{r.name}</span>
            <div className="h-1.5 w-20 rounded-full bg-slate-100 overflow-hidden">
              <div className={cn("h-full rounded-full", GRADE_DOT[r.grade.color])} style={{ width: `${r.score}%` }} />
            </div>
            <span className={cn("text-sm font-bold w-8 text-right", GRADE_TEXT[r.grade.color])}>{r.score}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-400 italic">Belum ada data.</p>}
      </div>
    </div>
  );
}
